import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { formatIBAN, cleanIBAN, lookupBankDetails } from '../../utils/ibanHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [ibanValidating, setIbanValidating] = useState(false);
  const [ibanValidationResult, setIbanValidationResult] = useState(null);
  
  const [bankDetails, setBankDetails] = useState({
    payout_method: 'bank_transfer',
    iban: '',
    iban_formatted: '',
    bank_name: '',
    bic: '',
    bank_country: '',
    account_holder: '',
    payout_email: ''
  });

  useEffect(() => {
    loadAffiliateData();
  }, []);

  const loadAffiliateData = async () => {
    const token = localStorage.getItem('affiliate_token');
    
    if (!token) {
      navigate('/affiliate/login');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/affiliate/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const affiliate = response.data.affiliate;
      
      setBankDetails({
        payout_method: affiliate.payout_method || 'bank_transfer',
        iban: affiliate.iban || '',
        iban_formatted: affiliate.iban_formatted || '',
        bank_name: affiliate.bank_name || '',
        bic: affiliate.bic || '',
        bank_country: affiliate.bank_country || '',
        account_holder: affiliate.account_holder || '',
        payout_email: affiliate.payout_email || ''
      });
      
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/affiliate/login');
      } else {
        setError('Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleIBANChange = async (e) => {
    const value = e.target.value;
    const formatted = formatIBAN(value);
    
    setBankDetails(prev => ({
      ...prev,
      iban: cleanIBAN(value),
      iban_formatted: formatted
    }));
    
    // Trigger bank lookup if IBAN is valid length
    const cleaned = cleanIBAN(value);
    if (cleaned.length >= 15 && cleaned.length <= 34) {
      setIbanValidating(true);
      
      try {
        const result = await lookupBankDetails(cleaned, API_URL);
        
        if (result.valid) {
          setBankDetails(prev => ({
            ...prev,
            iban: result.iban,
            iban_formatted: result.iban_formatted,
            bank_name: result.bank_name || prev.bank_name,
            bic: result.bic || prev.bic,
            bank_country: result.bank_country || prev.bank_country
          }));
          
          setIbanValidationResult({
            valid: true,
            message: result.bank_name 
              ? `✓ ${result.bank_name}` 
              : result.message
          });
        } else {
          setIbanValidationResult({
            valid: false,
            message: result.message
          });
        }
      } catch (error) {
        console.error('IBAN lookup error:', error);
      } finally {
        setIbanValidating(false);
      }
    } else {
      setIbanValidationResult(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    setError('');
    
    const token = localStorage.getItem('affiliate_token');
    
    try {
      await axios.put(
        `${API_URL}/api/affiliate/profile/bank-details`,
        bankDetails,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setSuccess('Bankdaten erfolgreich gespeichert!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/affiliate/dashboard-new')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg flex-shrink-0"
              title="Zurück zum Dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Auszahlungsdaten
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Hinterlegen oder ändern Sie Ihre Bankdaten für Provisionsauszahlungen
              </p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-700">{success}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Bank Details Form */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
          <div className="space-y-4 sm:space-y-6">
            {/* Auszahlungsmethode – nur Banküberweisung */}
            <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center flex-shrink-0">
                <Save className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200">Auszahlungsmethode: Banküberweisung</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Provisionen werden per Banküberweisung auf die hinterlegte IBAN ausgezahlt.</p>
              </div>
            </div>

            {/* IBAN & Bankdaten */}
            <>
              {/* IBAN */}
              <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    IBAN
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={bankDetails.iban_formatted || bankDetails.iban}
                      onChange={handleIBANChange}
                      placeholder="DE12 1234 5678 9012 3456 78"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 text-sm sm:text-base min-h-[44px]"
                    />
                    {ibanValidating && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                      </div>
                    )}
                  </div>
                  {ibanValidationResult && (
                    <p className={`mt-2 text-xs sm:text-sm ${ibanValidationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                      {ibanValidationResult.message}
                    </p>
                  )}
                  {bankDetails.bank_name && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-300">
                        <strong>Bank:</strong> {bankDetails.bank_name}
                        {bankDetails.bic && <span className="ml-3">BIC: {bankDetails.bic}</span>}
                        {bankDetails.bank_country && <span className="ml-3">Land: {bankDetails.bank_country}</span>}
                      </p>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    IBAN wird automatisch formatiert. Bankdaten werden automatisch erkannt.
                  </p>
                </div>

                {/* Account Holder */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kontoinhaber
                  </label>
                  <input
                    type="text"
                    value={bankDetails.account_holder}
                    onChange={(e) => setBankDetails({ ...bankDetails, account_holder: e.target.value })}
                    placeholder="Max Mustermann"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 text-sm sm:text-base min-h-[44px]"
                  />
                </div>
              </>

            {/* Info Box */}
            <div className="p-3 sm:p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-200">
                Ihre Bankdaten werden verschlüsselt gespeichert und ausschließlich für Provisionsauszahlungen verwendet.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
              <button
                onClick={() => navigate('/affiliate/dashboard-new')}
                className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base font-medium min-h-[44px]"
              >
                Zurück zum Dashboard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base font-medium min-h-[44px]"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Speichern
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateProfile;
